import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  ButtonGroup,
  Card,
  Col,
  OverlayTrigger,
  Row,
  Spinner,
  Table,
  Tooltip,
} from 'react-bootstrap';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  AlertTriangle,
  ArrowLeftRight,
  HelpCircle,
  TrendingDown,
  Users,
  Wallet,
} from 'lucide-react';
import { toast } from 'react-toastify';

import { customerAnalyticsAPI } from '../../services';

/**
 * Tab "Nhóm khách quen" (#023444 — lệnh sếp, việc gốc #020714).
 *
 * Đưa lên màn đúng 4 phép tính đã chạy ở điều tra #233947 + #014933, áp cho MỌI chi nhánh:
 *  (A) nhóm quen hình thành / mất đi theo tháng
 *  (B) mức độ tương quan: hành vi nhóm ĐÃ MẤT so với nhóm CÒN Ở LẠI
 *  (C) doanh thu dự báo theo 3 kịch bản
 *  (D) nhóm quay lại sau ba tháng vắng
 *
 * Nguyên tắc hiển thị (AC0): mọi con số đều kèm CÁCH ĐẾM, mọi cảnh báo trung thực của Điều tra đều
 * hiện ngay trên màn (đếm thiếu do thiếu SĐT, tháng gần đây chưa chốt, dự báo là ước tính ngoại
 * suy), chi nhánh thiếu dữ liệu thì nói thẳng là thiếu chứ không vẽ số cho đẹp.
 */

const FAMILIAR_THRESHOLDS = [2, 3, 5];
const ABSENCE_DAYS = [60, 90, 120];
const SCENARIOS = [
  { key: 'low', label: 'THẤP', hint: 'Trung vị nhịp chi của từng nhóm' },
  { key: 'middle', label: 'GIỮA', hint: 'Bình quân gộp = tổng chi tiêu ÷ tổng tháng hoạt động — số khuyến nghị' },
  { key: 'high', label: 'CAO', hint: 'Trung bình thô, bị các nhóm cửa sổ hoạt động ngắn kéo lên — chỉ để tham khảo trần trên' },
];
const WEEKDAY_LABELS = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'];

const money = (n) =>
  n === null || n === undefined ? '—' : `${Math.round(n).toLocaleString('vi-VN')}đ`;
const pct1 = (n) => (n === null || n === undefined ? '—' : `${(n * 100).toFixed(1)}%`);
const num1 = (n) => (n === null || n === undefined ? '—' : n.toFixed(1));

/** Chú thích "cách đếm" cạnh mỗi con số — bấm/di chuột đều xem được trên điện thoại. */
function How({ children }) {
  return (
    <OverlayTrigger
      trigger={['hover', 'focus', 'click']}
      placement="auto"
      overlay={<Tooltip>{children}</Tooltip>}
    >
      <Button variant="link" size="sm" className="p-0 ms-1 align-baseline text-muted">
        <HelpCircle size={13} />
      </Button>
    </OverlayTrigger>
  );
}

function SectionCard({ title, icon: Icon, color, how, children }) {
  return (
    <Card className="shadow-sm mb-4">
      <Card.Header
        className="py-2 px-3 d-flex align-items-center gap-2"
        style={{ background: `${color}15` }}
      >
        <Icon size={16} color={color} />
        <strong style={{ fontSize: '0.9rem', color }}>{title}</strong>
        {how && <How>{how}</How>}
      </Card.Header>
      <Card.Body className="px-2 px-md-3">{children}</Card.Body>
    </Card>
  );
}

/** Dòng "chưa đủ dữ liệu" dùng chung — KHÔNG bịa số 0 cho chỗ không tính được. */
function NoData({ children = 'Chưa đủ dữ liệu để kết luận' }) {
  return <div className="text-center text-muted py-3 small">{children}</div>;
}

export default function LoyalCohortTab({ queryParams }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [familiarThreshold, setFamiliarThreshold] = useState(3);
  const [absenceDays, setAbsenceDays] = useState(90);
  const [showMethodB, setShowMethodB] = useState(false);
  const [scenario, setScenario] = useState('middle');

  const params = useMemo(
    () => ({
      ...queryParams,
      familiar_threshold: familiarThreshold,
      absence_days: absenceDays,
      loss_method: showMethodB ? 'both' : 'A',
    }),
    [queryParams, familiarThreshold, absenceDays, showMethodB],
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      setData(await customerAnalyticsAPI.getLoyalCohort(params));
    } catch (err) {
      console.error('[LoyalCohort]', err);
      toast.error('Không tải được báo cáo nhóm khách quen');
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading && !data) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" size="sm" /> <span className="ms-2">Đang tính…</span>
      </div>
    );
  }
  if (!data) return <NoData>Không có dữ liệu</NoData>;

  const { correlation: cor, forecast: fc, return_after_absence: ret } = data;
  const lostScenario = fc.avg_monthly_spend.lost;

  return (
    <div>
      {/* ── Bộ chọn ngưỡng: mọi ngưỡng đều chọn được, không chôn cứng (AC0) ─────────── */}
      <Card className="mb-3 border-0" style={{ background: '#f8f9fa' }}>
        <Card.Body className="py-2 px-3">
          <Row className="g-2 align-items-center">
            <Col xs={12} md="auto" className="small text-muted">
              Nhóm quen ={' '}
              <ButtonGroup size="sm">
                {FAMILIAR_THRESHOLDS.map((t) => (
                  <Button
                    key={t}
                    variant={familiarThreshold === t ? 'primary' : 'outline-secondary'}
                    onClick={() => setFamiliarThreshold(t)}
                  >
                    ≥{t} lượt
                  </Button>
                ))}
              </ButtonGroup>
            </Col>
            <Col xs={12} md="auto" className="small text-muted">
              Vắng bao lâu là mất ={' '}
              <ButtonGroup size="sm">
                {ABSENCE_DAYS.map((d) => (
                  <Button
                    key={d}
                    variant={absenceDays === d ? 'primary' : 'outline-secondary'}
                    onClick={() => setAbsenceDays(d)}
                  >
                    {d} ngày
                  </Button>
                ))}
              </ButtonGroup>
            </Col>
            <Col xs={12} md="auto">
              <Button
                size="sm"
                variant={showMethodB ? 'primary' : 'outline-secondary'}
                onClick={() => setShowMethodB((v) => !v)}
              >
                {showMethodB ? '✓ ' : ''}So thêm Cách B
              </Button>
              <How>
                Cách A: vắng quá {absenceDays} ngày tính từ lần đến cuối. Cách B: vắng quá 3 lần nhịp
                đến trung bình của chính nhóm đó và tối thiểu 60 ngày — bắt được nhóm đến rất đều mà
                Cách A còn bỏ sót.
              </How>
            </Col>
            {loading && (
              <Col xs="auto">
                <Spinner animation="border" size="sm" />
              </Col>
            )}
          </Row>
        </Card.Body>
      </Card>

      {/* ── Cảnh báo trung thực, bắt buộc hiện (AC0) ────────────────────────────────── */}
      {data.insufficient_data_warning && (
        <Alert variant="warning" className="py-2 small">
          <AlertTriangle size={14} className="me-1" />
          {data.insufficient_data_warning}
        </Alert>
      )}
      <Alert variant="warning" className="py-2 small mb-3">
        <AlertTriangle size={14} className="me-1" />
        <strong>Số liệu ĐẾM THIẾU, không đếm thừa:</strong> {pct1(data.coverage.reservation.unidentified_pct)}{' '}
        lượt đặt bàn và {pct1(data.coverage.order.unidentified_pct)} đơn hàng của chi nhánh này không
        có số điện thoại nên không gộp được vào nhóm nào. Số điện thoại và tên khách không hiển thị ở
        đây — chỉ có số tổng hợp.
      </Alert>

      <div className="text-muted small mb-3">
        Đang xem <strong>{data.total_familiar_groups}</strong> nhóm quen (≥{data.familiar_threshold}{' '}
        lượt đến) trong khoảng {data.period.from_date} → {data.period.to_date}. Các số điện thoại
        từng dùng chung một thiết bị đã được gộp làm một nhóm ({data.device_grouping.pair_count} cặp
        SĐT–thiết bị, bỏ qua {data.device_grouping.skipped_shared_devices} thiết bị dùng chung trên{' '}
        {data.device_grouping.max_phones_per_device} SĐT).
      </div>

      {/* ── (A) Hình thành / mất đi theo tháng ──────────────────────────────────────── */}
      <SectionCard
        title="(A) Nhóm quen hình thành và mất đi theo tháng"
        icon={Users}
        color="#0d6efd"
        how={`Hình thành = nhóm lần đầu chạm mốc ${data.familiar_threshold} lượt đến trong tháng đó. Mất đi = nhóm có lần đến cuối rơi vào tháng đó và tới nay đã vắng quá ${data.absence_threshold_days} ngày. Một "lượt đến" là một NGÀY có ít nhất 1 phiếu đặt bàn không huỷ hoặc 1 đơn hoàn tất.`}
      >
        {!data.formation.rows.length ? (
          <NoData />
        ) : (
          <>
            <div style={{ width: '100%', height: 240 }}>
              <ResponsiveContainer>
                <BarChart data={data.formation.rows} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} tickFormatter={(m) => m.slice(5)} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <RTooltip />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="formed" name="Hình thành" fill="#198754" />
                  <Bar dataKey="lost" name="Mất đi" fill="#dc3545" />
                  {showMethodB && (
                    <Bar dataKey="lost_method_b" name="Mất đi (Cách B)" fill="#fd7e14" />
                  )}
                </BarChart>
              </ResponsiveContainer>
            </div>

            <Table size="sm" responsive hover className="mt-3 mb-2">
              <thead>
                <tr className="table-light">
                  <th>Tháng</th>
                  <th className="text-end">Hình thành</th>
                  <th className="text-end">Mất đi</th>
                  {showMethodB && <th className="text-end">Mất đi (Cách B)</th>}
                  <th className="text-end">Số ròng</th>
                </tr>
              </thead>
              <tbody>
                {data.formation.rows.map((r) => (
                  <tr key={r.month}>
                    <td>
                      {r.month}
                      {r.is_censored && (
                        <Badge bg="warning" text="dark" className="ms-1" style={{ fontSize: '0.65rem' }}>
                          ⚠ còn tăng
                        </Badge>
                      )}
                    </td>
                    <td className="text-end text-success">{r.formed}</td>
                    <td className="text-end text-danger">{r.lost}</td>
                    {showMethodB && <td className="text-end" style={{ color: '#fd7e14' }}>{r.lost_method_b ?? '—'}</td>}
                    <td className={`text-end fw-bold ${r.net < 0 ? 'text-danger' : 'text-success'}`}>
                      {r.net > 0 ? `+${r.net}` : r.net}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <div className="text-muted" style={{ fontSize: '0.78rem' }}>
              {data.formation.censored_note}
            </div>
          </>
        )}
      </SectionCard>

      {/* ── (B) Mức độ tương quan ───────────────────────────────────────────────────── */}
      <SectionCard
        title="(B) Mức độ tương quan — nhóm ĐÃ MẤT so với nhóm CÒN Ở LẠI"
        icon={ArrowLeftRight}
        color="#6f42c1"
        how="So đúng 6 chỉ số đã đo ở điều tra #233947, không thêm chỉ số nào khác. Chỉ số nào chi nhánh đang chọn không đủ mẫu thì ghi thẳng là chưa đủ dữ liệu."
      >
        {!cor.lost_group_count && !cor.staying_group_count ? (
          <NoData />
        ) : (
          <>
            <Table size="sm" responsive className="mb-3">
              <thead>
                <tr className="table-light">
                  <th>Chỉ tiêu</th>
                  <th className="text-end text-danger">ĐÃ MẤT ({cor.lost_group_count} nhóm)</th>
                  <th className="text-end text-success">CÒN Ở LẠI ({cor.staying_group_count} nhóm)</th>
                </tr>
              </thead>
              <tbody>
                <BehaviorRow
                  label="Khung giờ hay đến"
                  how="Giờ có nhiều lượt đến nhất (giờ Việt Nam) — xem biểu đồ dưới để thấy cả hình dạng, vì có nhóm đến rải nhiều khung giờ chứ không dồn vào một giờ."
                  lost={cor.lost.peak_hour === null ? null : `${cor.lost.peak_hour}h (${pct1(cor.lost.peak_hour_share)})`}
                  staying={cor.staying.peak_hour === null ? null : `${cor.staying.peak_hour}h (${pct1(cor.staying.peak_hour_share)})`}
                />
                <BehaviorRow
                  label="Thứ hay đến"
                  lost={cor.lost.peak_weekday === null ? null : `${WEEKDAY_LABELS[cor.lost.peak_weekday]} (${pct1(cor.lost.peak_weekday_share)})`}
                  staying={cor.staying.peak_weekday === null ? null : `${WEEKDAY_LABELS[cor.staying.peak_weekday]} (${pct1(cor.staying.peak_weekday_share)})`}
                />
                <BehaviorRow
                  label="Số khách/lượt (TB · trung vị)"
                  how="Đã loại các bản ghi số khách nhập lỗi (trên 50 khách/lượt) — lỗi dữ liệu có sẵn, không phải cách tính."
                  lost={cor.lost.avg_party_size === null ? null : `${num1(cor.lost.avg_party_size)} · ${cor.lost.median_party_size}`}
                  staying={cor.staying.avg_party_size === null ? null : `${num1(cor.staying.avg_party_size)} · ${cor.staying.median_party_size}`}
                />
                <BehaviorRow
                  label="Lượt dùng phòng riêng"
                  lost={cor.lost.private_room_pct === null ? null : pct1(cor.lost.private_room_pct)}
                  staying={cor.staying.private_room_pct === null ? null : pct1(cor.staying.private_room_pct)}
                />
                <BehaviorRow
                  label="Chi tiêu TB/đơn"
                  lost={cor.lost.avg_spend_per_order === null ? null : money(cor.lost.avg_spend_per_order)}
                  staying={cor.staying.avg_spend_per_order === null ? null : money(cor.staying.avg_spend_per_order)}
                />
                <BehaviorRow
                  label="Nhóm từng đặt bàn trước"
                  lost={cor.lost.pre_reservation_pct === null ? null : pct1(cor.lost.pre_reservation_pct)}
                  staying={cor.staying.pre_reservation_pct === null ? null : pct1(cor.staying.pre_reservation_pct)}
                />
              </tbody>
            </Table>

            <div className="small text-muted mb-1">Số lượt theo khung giờ (giờ Việt Nam)</div>
            <div style={{ width: '100%', height: 180 }}>
              <ResponsiveContainer>
                <LineChart
                  data={Array.from({ length: 24 }, (_, h) => ({
                    hour: `${h}h`,
                    lost: cor.lost.hour_distribution?.[h] ?? 0,
                    staying: cor.staying.hour_distribution?.[h] ?? 0,
                  }))}
                  margin={{ top: 8, right: 8, left: -22, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="hour" tick={{ fontSize: 10 }} interval={1} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <RTooltip />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="lost" name="Đã mất" stroke="#dc3545" dot={false} />
                  <Line type="monotone" dataKey="staying" name="Còn ở lại" stroke="#198754" dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <Row className="g-3 mt-1">
              <Col xs={12} md={6}>
                <div className="border rounded p-2 h-100">
                  <div className="small fw-bold mb-2">
                    Kiểu rời đi
                    <How>
                      Cần ít nhất 4 lượt đến mới phân loại được. So nhịp đến nửa sau với nửa đầu:
                      giãn ra quá 1,5 lần là "thưa dần", còn lại là "đứt đột ngột".
                    </How>
                  </div>
                  <CountRow label="Đứt đột ngột" value={cor.departure_style.sudden} color="#dc3545" />
                  <CountRow label="Thưa dần" value={cor.departure_style.gradual} color="#fd7e14" />
                  <CountRow label="Không đủ dữ liệu" value={cor.departure_style.insufficient_data} color="#6c757d" />
                </div>
              </Col>
              <Col xs={12} md={6}>
                <div className="border rounded p-2 h-100">
                  <div className="small fw-bold mb-2">
                    Xu hướng chi tiêu trước khi mất
                    <How>
                      Cần ít nhất 4 đơn hoàn tất. So chi tiêu trung bình nửa đầu với nửa cuối lịch sử
                      mua của chính nhóm đó, lệch quá 15% mới tính là tăng/giảm rõ.
                    </How>
                  </div>
                  <CountRow label="Tăng rõ (>15%)" value={cor.spend_trend.increasing} color="#198754" />
                  <CountRow label="Giảm rõ (>15%)" value={cor.spend_trend.decreasing} color="#dc3545" />
                  <CountRow label="Ổn định" value={cor.spend_trend.stable} color="#0d6efd" />
                  <CountRow label="Không đủ dữ liệu" value={cor.spend_trend.insufficient_data} color="#6c757d" />
                </div>
              </Col>
            </Row>

            <Alert variant="light" className="border py-2 px-3 small mt-3 mb-0">
              <strong>Có chuyển sang chi nhánh khác không?</strong>{' '}
              {cor.cross_branch_reappearance.applicable ? (
                <>
                  {cor.cross_branch_reappearance.reappeared_count}/
                  {cor.cross_branch_reappearance.total_lost_count} nhóm đã mất ở đây (
                  {pct1(cor.cross_branch_reappearance.reappearance_rate)}) có xuất hiện lại tại chi
                  nhánh khác sau lần đến cuối.
                </>
              ) : null}
              <div className="text-muted mt-1">{cor.cross_branch_reappearance.limitation_note}</div>
            </Alert>
          </>
        )}
      </SectionCard>

      {/* ── (C) Doanh thu dự báo ────────────────────────────────────────────────────── */}
      <SectionCard
        title="(C) Doanh thu dự báo"
        icon={Wallet}
        color="#d63384"
        how="Chi bình quân tháng của một nhóm = tổng tiền các đơn hoàn tất ÷ số tháng từ lần đến đầu tới lần đến cuối của chính nhóm đó (1 tháng = 30,44 ngày). Không chia cho quãng đã bỏ đi."
      >
        <Alert variant="danger" className="py-2 px-3 small">
          <AlertTriangle size={14} className="me-1" />
          {fc.disclaimer}
        </Alert>

        <div className="small text-muted mb-2">
          Kịch bản đang xem:{' '}
          <ButtonGroup size="sm">
            {SCENARIOS.map((s) => (
              <Button
                key={s.key}
                variant={scenario === s.key ? 'danger' : 'outline-secondary'}
                onClick={() => setScenario(s.key)}
                title={s.hint}
              >
                {s.label}
              </Button>
            ))}
          </ButtonGroup>
          <How>{SCENARIOS.find((s) => s.key === scenario)?.hint}</How>
        </div>

        <Row className="g-2 mb-3">
          <Col xs={12} md={4}>
            <MiniStat
              label="Chi bình quân/tháng của 1 nhóm đã mất"
              value={money(lostScenario[scenario])}
              sub={`${lostScenario.n} nhóm · ${lostScenario.short_window_groups} nhóm có cửa sổ hoạt động dưới 30 ngày (dễ bị thổi phồng ở kịch bản CAO)`}
            />
          </Col>
          <Col xs={12} md={4}>
            <MiniStat
              label="Đang mất mỗi tháng (toàn bộ nhóm đã mất)"
              value={money(fc.current_run_rate[scenario])}
              sub="Nếu họ vẫn chi như hồi còn đến"
            />
          </Col>
          <Col xs={12} md={4}>
            <MiniStat
              label="Doanh thu đã hụt trong khoảng đang xem"
              value={money(fc.lost_revenue[scenario])}
              sub={`${fc.lost_revenue.groups} nhóm rụng · họ từng đem về thật ${money(fc.lost_revenue.actual_revenue)}`}
            />
          </Col>
        </Row>

        {fc.lost_revenue.by_month.length > 0 && (
          <Table size="sm" responsive className="mb-3">
            <thead>
              <tr className="table-light">
                <th>Tháng rụng</th>
                <th className="text-end">Số nhóm</th>
                <th className="text-end">Bình quân gộp/nhóm/tháng</th>
                <th className="text-end">Hụt ước tính (GIỮA)</th>
              </tr>
            </thead>
            <tbody>
              {fc.lost_revenue.by_month.map((m) => (
                <tr key={m.month}>
                  <td>{m.month}</td>
                  <td className="text-end">{m.groups}</td>
                  <td className="text-end">{money(m.pooled_rate)}</td>
                  <td className="text-end text-danger">{money(m.middle)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}

        <div className="small fw-bold mb-2">
          Dự báo mỗi tháng tới
          <How>
            Giả định tốc độ rụng giữ nguyên {num1(fc.loss_rate_per_month)} nhóm/tháng (trung bình các
            tháng đã quan sát đủ: {fc.loss_rate_months_used.join(', ') || 'chưa có tháng nào đủ'}),
            và nhóm rụng thêm có nhịp chi xấp xỉ nhóm đã mất hiện tại.
          </How>
        </div>
        {!fc.loss_rate_months_used.length ? (
          <NoData>Chưa có tháng nào quan sát đủ để đo tốc độ rụng — chưa dự báo được</NoData>
        ) : (
          <Table size="sm" responsive className="mb-0">
            <thead>
              <tr className="table-light">
                <th>Mốc</th>
                <th className="text-end">Nhóm mất thêm</th>
                <th className="text-end">Khoảng THẤP – CAO</th>
                <th className="text-end">
                  Có trừ quay lại
                  <How>
                    Lấy dự báo kịch bản GIỮA trừ đi phần dự kiến quay lại theo đúng tỷ lệ quay lại đo
                    được ở khối (D) ({pct1(fc.return_rate_used)}). Hiện cạnh số thô để thấy cả hai chiều.
                  </How>
                </th>
              </tr>
            </thead>
            <tbody>
              {fc.horizons.map((h) => (
                <tr key={h.months_ahead}>
                  <td>
                    +{h.months_ahead} tháng
                    <How>{h.confidence_note}</How>
                  </td>
                  <td className="text-end">{Math.round(h.extra_lost_groups)}</td>
                  <td className="text-end">
                    <div className="fw-bold text-danger">{money(h.gross_middle)}</div>
                    <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                      {money(h.gross_low)} – {money(h.gross_high)}
                    </div>
                  </td>
                  <td className="text-end">{money(h.net_middle)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </SectionCard>

      {/* ── (D) Quay lại sau ba tháng ───────────────────────────────────────────────── */}
      <SectionCard
        title={`(D) Nhóm quay lại sau khi vắng quá ${ret.absence_threshold_days} ngày`}
        icon={TrendingDown}
        color="#20c997"
        how={`Đếm nhóm từng vắng quá ${ret.absence_threshold_days} ngày giữa 2 lượt đến rồi vẫn quay lại. Mẫu số gồm cả nhóm đang vắng quá mốc mà chưa quay lại.`}
      >
        {!ret.return_stats.total_absent_count ? (
          <NoData>Chưa có nhóm nào vắng quá mốc này</NoData>
        ) : (
          <>
            <Row className="g-2 mb-3">
              <Col xs={12} md={4}>
                <MiniStat
                  label="Tỷ lệ quay lại"
                  value={pct1(ret.return_stats.return_rate)}
                  sub={`${ret.return_stats.returned_count}/${ret.return_stats.total_absent_count} nhóm từng vượt mốc`}
                />
              </Col>
              <Col xs={12} md={4}>
                <MiniStat
                  label="Vắng bao lâu rồi mới quay lại"
                  value={
                    ret.absence_duration.median_days === null
                      ? '—'
                      : `${Math.round(ret.absence_duration.median_days)} ngày`
                  }
                  sub={
                    ret.absence_duration.mean_days === null
                      ? 'Chưa có nhóm nào quay lại'
                      : `trung vị · trung bình ${Math.round(ret.absence_duration.mean_days)} ngày`
                  }
                />
              </Col>
              <Col xs={12} md={4}>
                <MiniStat
                  label="Sau khi quay lại, chi tiêu/đơn"
                  value={
                    ret.before_after_comparison.spend_change_pct === null
                      ? '—'
                      : `${ret.before_after_comparison.spend_change_pct > 0 ? '+' : ''}${(ret.before_after_comparison.spend_change_pct * 100).toFixed(0)}%`
                  }
                  sub={
                    ret.before_after_comparison.sample
                      ? `${money(ret.before_after_comparison.avg_spend_before)} → ${money(ret.before_after_comparison.avg_spend_after)} · ${ret.before_after_comparison.sample} nhóm so được`
                      : 'Chưa đủ dữ liệu để so'
                  }
                />
              </Col>
            </Row>

            <div className="small text-muted mb-1">
              Tần suất đến: {num1(ret.before_after_comparison.avg_frequency_before)} lượt/tháng trước
              khi vắng → {num1(ret.before_after_comparison.avg_frequency_after)} lượt/tháng sau khi
              quay lại
              {ret.before_after_comparison.frequency_change_pct !== null && (
                <>
                  {' '}
                  ({ret.before_after_comparison.frequency_change_pct > 0 ? '+' : ''}
                  {(ret.before_after_comparison.frequency_change_pct * 100).toFixed(0)}%)
                </>
              )}
              <How>
                Gộp chung rồi mới chia (tổng lượt ÷ tổng tháng của tất cả nhóm) thay vì lấy trung
                bình từng nhóm — vài nhóm hoạt động vài ngày sẽ cho tỉ lệ lượt/tháng rất lớn và kéo
                lệch số trung bình.
              </How>
            </div>

            {ret.monthly_distribution.length > 0 && (
              <div style={{ width: '100%', height: 200 }}>
                <ResponsiveContainer>
                  <BarChart data={ret.monthly_distribution} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} tickFormatter={(m) => m.slice(5)} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <RTooltip />
                    <Bar dataKey="returned_count" name="Nhóm quay lại" fill="#20c997">
                      {ret.monthly_distribution.map((m) => (
                        <Cell key={m.month} fill="#20c997" />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </>
        )}
      </SectionCard>
    </div>
  );
}

function BehaviorRow({ label, how, lost, staying }) {
  return (
    <tr>
      <td>
        {label}
        {how && <How>{how}</How>}
      </td>
      <td className="text-end">{lost ?? <span className="text-muted">chưa đủ dữ liệu</span>}</td>
      <td className="text-end">{staying ?? <span className="text-muted">chưa đủ dữ liệu</span>}</td>
    </tr>
  );
}

function CountRow({ label, value, color }) {
  return (
    <div className="d-flex justify-content-between small py-1 border-bottom">
      <span className="text-muted">{label}</span>
      <strong style={{ color }}>{value}</strong>
    </div>
  );
}

function MiniStat({ label, value, sub }) {
  return (
    <div className="border rounded p-2 h-100">
      <div className="text-muted" style={{ fontSize: '0.75rem' }}>{label}</div>
      <div className="fw-bold" style={{ fontSize: '1.05rem' }}>{value}</div>
      {sub && <div className="text-muted" style={{ fontSize: '0.72rem' }}>{sub}</div>}
    </div>
  );
}
